import React, { Component } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Button, CssBaseline, TextField, Grid, Typography, CircularProgress, Container, Checkbox, FormControlLabel, Switch, Autocomplete, Select, MenuItem, InputLabel, FormControl, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButtonGroup, ToggleButton, Tooltip, Skeleton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import { Campaign as CampaignIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import { Styles } from '../../styles/material-styles';
import { Overlay } from '../../styles/global';
import Api from '../../services/api';
import CustomizedSnackbars from '../../components/material-snackbars';
import Dropzone from 'react-dropzone';
import HeaderMenu from '../../components/header-menu';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import Footer from '../../components/footer';
import { getById, create, update, getSettings, getTargetableOrganizations, listEmailTemplates, renderEmailTemplate, previewEmail, uploadContentAsset } from '../../services/announcement.service';
import SimpleAutoCompleteMultipleObservant from '../../components/auto-complete/autocomplete-multiple-observant';
import EmailTemplatePlaceholder from '../../components/emailConfig/EmailTemplatePlaceholder';
import useEmailAssetDisplayHook from '../../components/emailConfig/useEmailAssetDisplay';
import useAnnouncementAssetDisplayHook from './useAnnouncementAssetDisplay';
import { EmailDesignerPreview } from '../../components/email-template-designer';

const RichTextEditor = React.lazy(() => import('../../components/rich-text-editor/RichTextEditor'));
const CodeEditor = React.lazy(() => import('../../components/code-editor/CodeEditor'));

const ANNOUNCEMENT_EMAIL_PLACEHOLDERS = [
  { name: 'announcement.title', displayname: 'Título do aviso', placetype: 'announcement' },
  { name: 'announcement.content', displayname: 'Texto do aviso', placetype: 'announcement' },
  { name: 'announcement.link', displayname: 'Link do aviso', placetype: 'announcement' },
  { name: 'announcement.publishdate', displayname: 'Data de publicação', placetype: 'announcement' },
  { name: 'user.name', displayname: 'Nome do destinatário', placetype: 'user' },
  { name: 'user.email', displayname: 'Email do destinatário', placetype: 'user' },
  { name: 'hirer.name', displayname: 'Nome do contratante', placetype: 'hirer' },
];

const EMAIL_EDITOR_HEIGHT = 560;
const EMAIL_MOBILE_WIDTH = 390;
const PUBLISHED_STATUS_ID = 2;

const toggleSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    px: 2,
    borderRadius: '10px !important',
    border: '1px solid #e0e0e0 !important',
    mr: 1,
  },
  '& .Mui-selected': {
    backgroundColor: '#eef7e3 !important',
    color: '#5b9b1e',
    borderColor: '#8BC34A !important',
  },
};

function AnnouncementEmailBodyEditor({ advanced, value, onChange, fullDocument, locale, device, editorKey, visualRef, codeRef, onPasteImage }) {
  const refreshDisplay = () => { if (visualRef.current) visualRef.current.refreshDisplay(); };
  const templateAssetDisplay = useEmailAssetDisplayHook(value, refreshDisplay);
  const pastedAssetDisplay = useAnnouncementAssetDisplayHook(value, refreshDisplay);
  const toDisplay = (html) => pastedAssetDisplay.toDisplay(templateAssetDisplay.toDisplay(html));
  const fromDisplay = (html) => templateAssetDisplay.fromDisplay(pastedAssetDisplay.fromDisplay(html));

  const fallback = <Skeleton variant="rectangular" height={EMAIL_EDITOR_HEIGHT} animation="wave" sx={{ borderRadius: '12px' }} />;

  return (
    <React.Suspense fallback={fallback}>
      {advanced ? (
        <CodeEditor ref={codeRef} language="html" value={value} onChange={onChange} height={EMAIL_EDITOR_HEIGHT} />
      ) : (
        <Box
          sx={{
            width: device === 'mobile' ? EMAIL_MOBILE_WIDTH : '100%',
            maxWidth: '100%',
            mx: 'auto',
            transition: 'width .2s ease',
            '& .jodit-container': { borderRadius: '12px', borderColor: '#e0e0e0' },
          }}
        >
          <RichTextEditor
            key={editorKey}
            ref={visualRef}
            value={value}
            onChange={onChange}
            fullDocument={fullDocument}
            height={EMAIL_EDITOR_HEIGHT}
            locale={locale}
            toDisplay={toDisplay}
            fromDisplay={fromDisplay}
            onPasteImage={onPasteImage}
          />
        </Box>
      )}
    </React.Suspense>
  );
}

function AnnouncementContentEditor({ value, onChange, locale, visualRef, onPasteImage }) {
  const assetDisplay = useAnnouncementAssetDisplayHook(value, () => {
    if (visualRef.current) visualRef.current.refreshDisplay();
  });

  return (
    <React.Suspense fallback={<Skeleton variant="rectangular" height={300} animation="wave" sx={{ borderRadius: '10px' }} />}>
      <RichTextEditor
        ref={visualRef}
        value={value}
        onChange={onChange}
        height={300}
        locale={locale}
        toDisplay={assetDisplay.toDisplay}
        fromDisplay={assetDisplay.fromDisplay}
        onPasteImage={onPasteImage}
      />
    </React.Suspense>
  );
}

class AnnouncementEdit extends Component {

  constructor(props) {
    super(props);

    const { userSession } = this.props;
    if (userSession == null || userSession.user == null || userSession.user.userrole == null) {
      Api.logoff();
    }

    this.originTab = new URLSearchParams(this.props.location ? this.props.location.search : '').get('tab') === 'manage'
      ? 'manage'
      : 'active';

    this.state = {
      loading: false,
      saving: false,
      id: this.props.match.params.id ? Number(this.props.match.params.id) : null,
      title: '',
      content: '',
      typeId: 1,
      audienceId: 1,
      pinned: false,
      requireAcknowledgment: false,
      publishDate: '',
      closeDate: '',
      scheduleEnabled: false,
      closeDateEnabled: false,
      allSelected: false,
      orgOptions: [],
      selectedOrgIds: [],
      existingAttachments: [],
      removedAttachmentIds: [],
      newFiles: [],
      settings: null,
      snackOpen: false,
      snackVariant: 'error',
      snackMessage: '',
      sendEmail: false,
      resendEmail: false,
      announcementStatusId: null,
      emailAuthoringChoice: 'write',
      emailTemplateId: null,
      emailSubject: '',
      emailBodyHtml: '',
      emailTemplateName: '',
      saveAsReusableTemplate: false,
      emailTemplateOptions: [],
      emailCopyRecipients: [],
      emailAdvanced: false,
      emailDevice: 'desktop',
      restoreConfirmOpen: false,
      previewOpen: false,
      previewLoading: false,
      previewData: null,
    };

    this.emailVisualRef = React.createRef();
    this.emailCodeRef = React.createRef();
    this.contentVisualRef = React.createRef();
  }

  componentDidMount() {
    this.loadSettings();
    this.loadOrganizations();
    this.loadEmailTemplates();
    if (this.state.id) {
      this.loadNotice();
    }
  }

  loadEmailTemplates = async () => {
    try {
      const res = await listEmailTemplates();
      const items = res?.data?.data ?? res?.data ?? [];
      this.setState({ emailTemplateOptions: items });
    } catch (e) {
      console.error('Failed to load email templates', e);
    }
  }

  loadEmailTemplateSnapshot = async (id) => {
    const intl = this.props.intl;
    if (!id) {
      this.setState({ emailTemplateId: null });
      return;
    }
    try {
      const res = await renderEmailTemplate(id);
      const data = res?.data?.data ?? res?.data;
      this.setState({
        emailTemplateId: id,
        emailSubject: data?.subject || '',
        emailBodyHtml: data?.html || '',
      });
    } catch (e) {
      const msg = e?.response?.data?.errors?.[0]?.message || e?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' });
      this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: msg });
    }
  }

  openRestoreConfirm = () => this.setState({ restoreConfirmOpen: true });

  closeRestoreConfirm = () => this.setState({ restoreConfirmOpen: false });

  confirmRestoreFromTemplate = () => {
    this.setState({ restoreConfirmOpen: false });
    this.loadEmailTemplateSnapshot(this.state.emailTemplateId);
  }

  insertIntoEmailBody = (code) => {
    const ref = this.state.emailAdvanced ? this.emailCodeRef : this.emailVisualRef;
    if (ref.current) ref.current.insertText(code);
    else this.setState((prev) => ({ emailBodyHtml: `${prev.emailBodyHtml || ''}${code}` }));
  }

  handlePasteImage = async (file) => {
    const intl = this.props.intl;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!file || !allowedTypes.includes(file.type)) {
      this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.form.pasteImage.invalidType' }) });
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.form.pasteImage.tooLarge' }) });
      return null;
    }
    try {
      const res = await uploadContentAsset(file);
      const data = res?.data?.data;
      if (!res?.data?.success || !data?.id) {
        this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.form.pasteImage.error' }) });
        return null;
      }
      return `<img src="mexx-announcement-asset://${data.id}" alt="" />`;
    } catch (e) {
      this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.form.pasteImage.error' }) });
      return null;
    }
  }

  handleEmailAuthoringChange = (event, choice) => {
    if (!choice || choice === this.state.emailAuthoringChoice) return;
    const { emailTemplateId } = this.state;
    this.setState({
      emailAuthoringChoice: choice,
      emailAdvanced: false,
      ...(choice === 'write' && emailTemplateId ? { emailTemplateId: null, emailBodyHtml: '' } : {}),
    });
  }

  loadSettings = async () => {
    try {
      const res = await getSettings();
      const settings = res?.data?.data ?? res?.data;
      this.setState({ settings });
    } catch (e) {
      console.error('Failed to load settings', e);
    }
  }

  loadOrganizations = async () => {
    try {
      const res = await getTargetableOrganizations();
      const orgOptions = res?.data?.data ?? res?.data ?? [];
      this.setState({ orgOptions });
    } catch (e) {
      console.error('Failed to load organizations', e);
    }
  }

  loadNotice = async () => {
    try {
      this.setState({ loading: true });
      const res = await getById(this.state.id);
      const d = res?.data?.data ?? res?.data;

      if (!d) {
        const intl = this.props.intl;
        this.setState({
          loading: false,
          snackOpen: true,
          snackVariant: 'error',
          snackMessage: intl.formatMessage({ id: 'process.error' })
        });
        setTimeout(() => this.props.history.goBack(), 1500);
        return;
      }

      this.setState({
        loading: false,
        title: d.title || '',
        content: d.content || '',
        typeId: d.typeid || 1,
        audienceId: d.audienceid || 1,
        pinned: d.pinned === true,
        requireAcknowledgment: d.requireacknowledgment === true,
        publishDate: d.publishdate ? moment(d.publishdate).format('YYYY-MM-DDTHH:mm') : '',
        closeDate: d.closedate ? moment(d.closedate).format('YYYY-MM-DDTHH:mm') : '',
        scheduleEnabled: !!d.publishdate,
        closeDateEnabled: !!d.closedate,
        allSelected: d.allorganizations === true || d.allbyglobaladmin === true,
        selectedOrgIds: Array.isArray(d.organizationids) ? d.organizationids : [],
        existingAttachments: Array.isArray(d.attachments) ? d.attachments : [],
        newFiles: [],
        removedAttachmentIds: [],
        announcementStatusId: d.statusid ?? null,
        sendEmail: d.sendemail === true,
        emailAuthoringChoice: (d.emailmode === 2 && d.emailtemplateid) ? 'existing' : 'write',
        emailTemplateId: d.emailtemplateid || null,
        emailSubject: d.emailsubject || '',
        emailBodyHtml: d.emailbodyhtml || '',
        emailCopyRecipients: Array.isArray(d.emailcopyrecipients)
          ? d.emailcopyrecipients.map(c => ({ id: c.userid || 0, name: c.email || '', email: c.email || '' }))
          : [],
      });
    } catch (e) {
      console.error('Failed to load notice', e);
      const intl = this.props.intl;
      this.setState({
        loading: false,
        snackOpen: true,
        snackVariant: 'error',
        snackMessage: intl.formatMessage({ id: 'process.error' })
      });
      setTimeout(() => this.props.history.goBack(), 1500);
    }
  }

  handleSave = async () => {
    const { title, content, typeId, audienceId, pinned, requireAcknowledgment, publishDate, closeDate, allSelected, selectedOrgIds, existingAttachments, removedAttachmentIds, newFiles, settings } = this.state;
    const intl = this.props.intl;

    if (!title.trim()) {
      this.setState({
        snackOpen: true,
        snackVariant: 'error',
        snackMessage: intl.formatMessage({ id: 'noticeboard.form.validation.title' })
      });
      return;
    }

    const plain = (content || '').replace(/<[^>]*>/g, '').trim();
    if (!plain) {
      this.setState({
        snackOpen: true,
        snackVariant: 'error',
        snackMessage: intl.formatMessage({ id: 'noticeboard.form.validation.content' })
      });
      return;
    }

    if (!allSelected && selectedOrgIds.length === 0) {
      this.setState({
        snackOpen: true,
        snackVariant: 'error',
        snackMessage: intl.formatMessage({ id: 'noticeboard.form.validation.organizations' })
      });
      return;
    }

    if (closeDate) {
      const effectivePublish = publishDate ? new Date(publishDate) : new Date();
      if (new Date(closeDate) <= effectivePublish) {
        this.setState({
          snackOpen: true,
          snackVariant: 'error',
          snackMessage: intl.formatMessage({ id: 'noticeboard.form.validation.closeDate' })
        });
        return;
      }
    }

    const keptExisting = existingAttachments.filter(a => !removedAttachmentIds.includes(a.attachmentid)).map(a => a.attachmentid);
    const maxAtt = settings?.maxattachments ?? 5;
    if (keptExisting.length + newFiles.length > maxAtt) {
      this.setState({
        snackOpen: true,
        snackVariant: 'error',
        snackMessage: intl.formatMessage({ id: 'noticeboard.form.attachments.limit' }, { max: maxAtt, size: settings?.maxattachmentsizemb ?? 10 })
      });
      return;
    }

    const maxSize = (settings?.maxattachmentsizemb ?? 10) * 1024 * 1024;
    if (newFiles.some(f => f.size > maxSize)) {
      this.setState({
        snackOpen: true,
        snackVariant: 'error',
        snackMessage: intl.formatMessage({ id: 'noticeboard.form.attachments.limit' }, { max: maxAtt, size: settings?.maxattachmentsizemb ?? 10 })
      });
      return;
    }

    const { sendEmail, resendEmail, emailAuthoringChoice, emailTemplateId, emailSubject, emailBodyHtml, emailTemplateName, saveAsReusableTemplate, emailCopyRecipients } = this.state;
    const isExistingTemplateChoice = emailAuthoringChoice === 'existing';
    let effectiveEmailMode = null;
    if (sendEmail) {
      if (isExistingTemplateChoice && !emailTemplateId) {
        this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.email.validation.selectTemplate' }) });
        return;
      }
      effectiveEmailMode = (isExistingTemplateChoice && emailTemplateId) ? 2 : (saveAsReusableTemplate ? 2 : 1);
      if (effectiveEmailMode === 2 && !emailTemplateId && !emailTemplateName.trim()) {
        this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.email.validation.templateName' }) });
        return;
      }
      if (!emailSubject.trim() || !emailBodyHtml.trim()) {
        this.setState({ snackOpen: true, snackVariant: 'error', snackMessage: intl.formatMessage({ id: 'noticeboard.email.validation.content' }) });
        return;
      }
    }

    const payload = {
      title: title.trim(),
      content,
      typeId,
      audienceId,
      pinned,
      requireAcknowledgment,
      publishDate: publishDate || null,
      closeDate: closeDate || null,
      allOrganizations: allSelected,
      organizationIds: allSelected ? [] : selectedOrgIds,
      keepAttachmentIds: keptExisting,
      files: newFiles,
      sendEmail,
      resendEmail,
      emailMode: effectiveEmailMode,
      emailTemplateId: effectiveEmailMode === 2 ? emailTemplateId : null,
      emailSubject: sendEmail ? emailSubject : null,
      emailBodyHtml: sendEmail ? emailBodyHtml : null,
      emailTemplateName: (effectiveEmailMode === 2 && !emailTemplateId) ? emailTemplateName.trim() : null,
      emailCopyRecipients: emailCopyRecipients.map(c => ({ id: c.id && c.id > 0 ? c.id : null, email: c.email || c.name || '' })),
    };

    this.setState({ saving: true });
    try {
      const res = this.state.id ? await update(this.state.id, payload) : await create(payload);
      const ok = res?.data?.success !== false;
      if (ok) {
        if (this.state.id) {
          this.setState({
            saving: false,
            snackOpen: true,
            snackVariant: 'success',
            snackMessage: intl.formatMessage({ id: 'edit.success' }),
          });
          this.loadNotice();
        } else {
          this.setState({
            saving: false,
            snackOpen: true,
            snackVariant: 'success',
            snackMessage: intl.formatMessage({ id: 'add.success' }),
          });
          setTimeout(() => this.backToList(), 1200);
        }
      } else {
        const msg = (res?.data?.errors && res.data.errors[0] && (res.data.errors[0].message || res.data.errors[0])) || intl.formatMessage({ id: 'process.error' });
        this.setState({ saving: false, snackOpen: true, snackVariant: 'error', snackMessage: msg });
      }
    } catch (e) {
      const msg = e?.response?.data?.errors?.[0]?.message || e?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'process.error' });
      this.setState({ saving: false, snackOpen: true, snackVariant: 'error', snackMessage: msg });
    }
  }

  openPreview = async () => {
    const { title, content, emailSubject, emailBodyHtml } = this.state;
    this.setState({ previewOpen: true, previewLoading: true, previewData: null });
    try {
      const res = await previewEmail({ title, content, emailSubject, emailBodyHtml });
      const data = res?.data?.data ?? res?.data;
      this.setState({ previewLoading: false, previewData: data });
    } catch (e) {
      this.setState({ previewLoading: false, previewData: null });
    }
  }

  closePreview = () => this.setState({ previewOpen: false });

  handleDropFiles = (acceptedFiles) => {
    this.setState(prevState => ({
      newFiles: [...prevState.newFiles, ...acceptedFiles]
    }));
  }

  removeExistingAttachment = (attachmentId) => {
    this.setState(prevState => ({
      removedAttachmentIds: [...prevState.removedAttachmentIds, attachmentId]
    }));
  }

  removeNewFile = (index) => {
    this.setState(prevState => ({
      newFiles: prevState.newFiles.filter((_, i) => i !== index)
    }));
  }

  closeSnackbar = () => {
    this.setState({ snackOpen: false });
  }

  backToList = () => {
    const lang = this.props.match.params.lang;
    const qs = this.originTab === 'manage' ? '?tab=manage' : '';
    this.props.history.push(`/${lang}/announcement${qs}`);
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const { loading, saving, title, content, typeId, audienceId, pinned, requireAcknowledgment, publishDate, closeDate, scheduleEnabled, closeDateEnabled, allSelected, orgOptions, selectedOrgIds, existingAttachments, removedAttachmentIds, newFiles, settings, snackOpen, snackVariant, snackMessage, sendEmail, emailAuthoringChoice, emailTemplateId, emailSubject, emailBodyHtml, emailTemplateName, saveAsReusableTemplate, emailTemplateOptions, emailCopyRecipients, emailAdvanced, emailDevice, restoreConfirmOpen, previewOpen, previewLoading, previewData } = this.state;
    const intl = this.props.intl;
    const roundedInput = { '& .MuiOutlinedInput-root': { borderRadius: '10px' } };
    const nowLocal = moment().format('YYYY-MM-DDTHH:mm');

    if (Api == null) {
      const l = this.props.match.params.lang;
      document.location.href = `/${l}/unavailable`
    }

    const visibleExisting = existingAttachments.filter(a => !removedAttachmentIds.includes(a.attachmentid));
    const selectedOrgValues = orgOptions.filter(o => selectedOrgIds.includes(o.id));

    return (
      <div className={classes.root}>
        <HeaderMenu />
        <main
          className={clsx(classes.content, {
            [classes.contentShift]: headerMenu.open,
          }, classes.actionsContent, {
            [classes.actionscontentShift]: actionsHeaderMenu.open,
          })}
        >
          <div className={classes.drawerHeader} />

          <Container component='main' maxWidth='md'>
            <CssBaseline />
            <div className={classes.paper} style={{ marginTop: 0 }}>
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => this.backToList()}
                  sx={{ textTransform: 'none', color: 'primary.main' }}
                >
                  {intl.formatMessage({ id: 'back' })}
                </Button>

                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  }}
                >
                  <CampaignIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                </Box>
                <Typography component='h1' sx={{ fontSize: 20 }}>
                  {this.state.id ? intl.formatMessage({ id: 'noticeboard.form.edit' }) : intl.formatMessage({ id: 'noticeboard.form.create' })}
                </Typography>
              </Box>

              {loading ? (
                <Overlay>
                  <CircularProgress color='secondary' />
                </Overlay>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '14px',
                    bgcolor: 'background.paper',
                    p: { xs: 2, sm: 3 },
                  }}
                >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      required
                      fullWidth
                      label={intl.formatMessage({ id: 'noticeboard.form.title' })}
                      variant='outlined'
                      value={title}
                      onChange={(e) => this.setState({ title: e.target.value })}
                      inputProps={{ maxLength: 255 }}
                      sx={roundedInput}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id='noticeboard-type-label'>
                        {intl.formatMessage({ id: 'noticeboard.form.type' })}
                      </InputLabel>
                      <Select
                        labelId='noticeboard-type-label'
                        label={intl.formatMessage({ id: 'noticeboard.form.type' })}
                        value={typeId}
                        onChange={(e) => this.setState({ typeId: Number(e.target.value) })}
                        sx={roundedInput}
                      >
                        <MenuItem value={1}>{intl.formatMessage({ id: 'noticeboard.type.1' })}</MenuItem>
                        <MenuItem value={2}>{intl.formatMessage({ id: 'noticeboard.type.2' })}</MenuItem>
                        <MenuItem value={3}>{intl.formatMessage({ id: 'noticeboard.type.3' })}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id='noticeboard-audience-label'>
                        {intl.formatMessage({ id: 'noticeboard.form.audience' })}
                      </InputLabel>
                      <Select
                        labelId='noticeboard-audience-label'
                        label={intl.formatMessage({ id: 'noticeboard.form.audience' })}
                        value={audienceId}
                        onChange={(e) => this.setState({ audienceId: Number(e.target.value) })}
                        sx={roundedInput}
                      >
                        <MenuItem value={1}>{intl.formatMessage({ id: 'noticeboard.audience.1' })}</MenuItem>
                        <MenuItem value={2}>{intl.formatMessage({ id: 'noticeboard.audience.2' })}</MenuItem>
                        <MenuItem value={3}>{intl.formatMessage({ id: 'noticeboard.audience.3' })}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={pinned}
                          onChange={(e) => this.setState({ pinned: e.target.checked })}
                        />
                      }
                      label={intl.formatMessage({ id: 'noticeboard.form.pinned' })}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={requireAcknowledgment}
                          onChange={(e) => this.setState({ requireAcknowledgment: e.target.checked })}
                        />
                      }
                      label={intl.formatMessage({ id: 'noticeboard.form.requireAcknowledgment' })}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <Typography variant='subtitle1'>
                      {intl.formatMessage({ id: 'noticeboard.form.content' })}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <Box sx={{ mb: '50px', '& .jodit-container': { borderRadius: '10px', borderColor: 'divider' } }}>
                      <AnnouncementContentEditor
                        value={content}
                        onChange={(v) => this.setState({ content: v })}
                        locale={intl.locale}
                        visualRef={this.contentVisualRef}
                        onPasteImage={this.handlePasteImage}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allSelected}
                          onChange={(e) => this.setState({ allSelected: e.target.checked })}
                        />
                      }
                      label={intl.formatMessage({ id: 'noticeboard.form.organizations.all' })}
                    />
                  </Grid>

                  {!allSelected && (
                    <Grid item xs={12} sm={12}>
                      <Autocomplete
                        multiple
                        options={orgOptions}
                        getOptionLabel={(o) => o.name || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={selectedOrgValues}
                        onChange={(e, val) => this.setState({ selectedOrgIds: val.map(o => o.id) })}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={intl.formatMessage({ id: 'noticeboard.form.organizations' })}
                            sx={roundedInput}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={this.state.sendEmail}
                          onChange={(e) => this.setState({ sendEmail: e.target.checked })}
                        />
                      }
                      label={intl.formatMessage({ id: 'noticeboard.email.send' })}
                    />
                  </Grid>

                  {sendEmail && (
                    <>
                      <Grid item xs={12} sm={12}>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={emailAuthoringChoice}
                          onChange={this.handleEmailAuthoringChange}
                          sx={toggleSx}
                        >
                          <ToggleButton value="existing">{intl.formatMessage({ id: 'noticeboard.email.mode.existing' })}</ToggleButton>
                          <ToggleButton value="write">{intl.formatMessage({ id: 'noticeboard.email.mode.inline' })}</ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>

                      {emailAuthoringChoice === 'existing' && (
                        <Grid item xs={12} sm={12}>
                          <FormControl fullWidth>
                            <InputLabel id="noticeboard-email-template-label">
                              {intl.formatMessage({ id: 'noticeboard.email.template.select' })}
                            </InputLabel>
                            <Select
                              labelId="noticeboard-email-template-label"
                              value={emailTemplateId || ''}
                              onChange={(e) => this.loadEmailTemplateSnapshot(Number(e.target.value) || null)}
                            >
                              {emailTemplateOptions.map((t) => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!!emailTemplateId && (
                            <Button
                              size="small"
                              onClick={this.openRestoreConfirm}
                              sx={{ textTransform: 'none', mt: 1 }}
                            >
                              {intl.formatMessage({ id: 'noticeboard.email.template.restore' })}
                            </Button>
                          )}
                        </Grid>
                      )}

                      <Grid item xs={12} sm={12}>
                        <TextField
                          fullWidth
                          label={intl.formatMessage({ id: 'noticeboard.email.subject' })}
                          value={emailSubject}
                          onChange={(e) => this.setState({ emailSubject: e.target.value })}
                          sx={roundedInput}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          <EmailTemplatePlaceholder
                            onInsert={this.insertIntoEmailBody}
                            placeholders={ANNOUNCEMENT_EMAIL_PLACEHOLDERS}
                            groupNames={{
                              announcement: intl.formatMessage({ id: 'noticeboard.email.placeholders.group.announcement' }),
                              user: intl.formatMessage({ id: 'noticeboard.email.placeholders.group.user' }),
                              hirer: intl.formatMessage({ id: 'noticeboard.email.placeholders.group.hirer' }),
                            }}
                            title={intl.formatMessage({ id: 'noticeboard.email.placeholders.title' })}
                            searchPlaceholder={intl.formatMessage({ id: 'noticeboard.email.placeholders.search' })}
                          />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {!emailAdvanced && (
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={emailDevice}
                                onChange={(e, v) => v && this.setState({ emailDevice: v })}
                              >
                                <ToggleButton value="desktop" aria-label="desktop">
                                  <Tooltip title={intl.formatMessage({ id: 'noticeboard.email.device.desktop' })}>
                                    <DesktopWindowsOutlinedIcon fontSize="small" />
                                  </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="mobile" aria-label="mobile">
                                  <Tooltip title={intl.formatMessage({ id: 'noticeboard.email.device.mobile' })}>
                                    <PhoneIphoneOutlinedIcon fontSize="small" />
                                  </Tooltip>
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                            <FormControlLabel
                              sx={{ m: 0, whiteSpace: 'nowrap' }}
                              control={<Switch size="small" checked={emailAdvanced} onChange={(e) => this.setState({ emailAdvanced: e.target.checked })} />}
                              label={<Typography sx={{ fontSize: 13, color: '#555' }}>{intl.formatMessage({ id: 'noticeboard.email.advancedHtml' })}</Typography>}
                            />
                          </Box>
                        </Box>

                        <AnnouncementEmailBodyEditor
                          advanced={emailAdvanced}
                          value={emailBodyHtml}
                          onChange={(v) => this.setState({ emailBodyHtml: v })}
                          fullDocument={emailAuthoringChoice === 'existing'}
                          locale={intl.locale}
                          device={emailDevice}
                          editorKey={`${emailAuthoringChoice}-${emailDevice}`}
                          visualRef={this.emailVisualRef}
                          codeRef={this.emailCodeRef}
                          onPasteImage={this.handlePasteImage}
                        />
                      </Grid>

                      {emailAuthoringChoice === 'write' && (
                        <>
                          <Grid item xs={12} sm={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={saveAsReusableTemplate}
                                  onChange={(e) => this.setState({ saveAsReusableTemplate: e.target.checked })}
                                />
                              }
                              label={intl.formatMessage({ id: 'noticeboard.email.saveAsTemplate' })}
                            />
                          </Grid>
                          {saveAsReusableTemplate && (
                            <Grid item xs={12} sm={12}>
                              <TextField
                                fullWidth
                                required
                                label={intl.formatMessage({ id: 'noticeboard.email.template.name' })}
                                value={emailTemplateName}
                                onChange={(e) => this.setState({ emailTemplateName: e.target.value })}
                                sx={roundedInput}
                              />
                            </Grid>
                          )}
                        </>
                      )}

                      {this.state.id && this.state.announcementStatusId === PUBLISHED_STATUS_ID && (
                        <Grid item xs={12} sm={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={this.state.resendEmail}
                                onChange={(e) => this.setState({ resendEmail: e.target.checked })}
                              />
                            }
                            label={intl.formatMessage({ id: 'noticeboard.email.resend' })}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} sm={12}>
                        <SimpleAutoCompleteMultipleObservant
                          label={intl.formatMessage({ id: 'noticeboard.email.copyRecipients' })}
                          options={[]}
                          stateName="emailCopyRecipients"
                          changeSelect={(name, value) => this.setState({ [name]: value })}
                          selected={emailCopyRecipients}
                          freeSolo={true}
                          urlapi={Api.apiurlbase() + 'api/users/search/user'}
                          minDigits={3}
                        />
                      </Grid>

                      <Grid item xs={12} sm={12}>
                        <Button variant="outlined" onClick={this.openPreview} sx={{ textTransform: 'none' }}>
                          {intl.formatMessage({ id: 'noticeboard.email.preview' })}
                        </Button>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={scheduleEnabled}
                          onChange={(e) => this.setState({ scheduleEnabled: e.target.checked, publishDate: e.target.checked ? publishDate : '' })}
                        />
                      }
                      label={'Agendar notícia?'}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={closeDateEnabled}
                          onChange={(e) => this.setState({ closeDateEnabled: e.target.checked, closeDate: e.target.checked ? closeDate : '' })}
                        />
                      }
                      label={'Definir data de encerramento?'}
                    />
                  </Grid>

                  {scheduleEnabled && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: nowLocal }}
                        label={intl.formatMessage({ id: 'noticeboard.form.publishDate' })}
                        value={publishDate}
                        onChange={(e) => {
                          const v = e.target.value;
                          this.setState((prev) => ({
                            publishDate: v,
                            closeDate: prev.closeDate && v && prev.closeDate <= v ? '' : prev.closeDate,
                          }));
                        }}
                        sx={roundedInput}
                      />
                    </Grid>
                  )}

                  {closeDateEnabled && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: scheduleEnabled && publishDate ? publishDate : nowLocal }}
                        label={intl.formatMessage({ id: 'noticeboard.form.closeDate' })}
                        value={closeDate}
                        onChange={(e) => this.setState({ closeDate: e.target.value })}
                        helperText={intl.formatMessage({ id: 'noticeboard.form.closeDate.helper' })}
                        sx={roundedInput}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={12}>
                    <Typography variant='subtitle2'>
                      {intl.formatMessage({ id: 'noticeboard.form.attachments' })}
                    </Typography>
                    <Typography variant='caption' display='block' sx={{ mb: 1 }}>
                      {intl.formatMessage({ id: 'noticeboard.form.attachments.limit' }, { max: settings?.maxattachments ?? 5, size: settings?.maxattachmentsizemb ?? 10 })}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <Dropzone onDrop={this.handleDropFiles}>
                      {({ getRootProps, getInputProps }) => (
                        <div
                          {...getRootProps()}
                          style={{
                            border: '2px dashed #ccc',
                            padding: '20px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            borderRadius: '12px'
                          }}
                        >
                          <input {...getInputProps()} />
                          <Typography>
                            {intl.formatMessage({ id: 'noticeboard.form.attachments.dragDrop' })}
                          </Typography>
                        </div>
                      )}
                    </Dropzone>
                  </Grid>

                  {visibleExisting.length > 0 && (
                    <Grid item xs={12} sm={12}>
                      <Typography variant='subtitle2'>
                        {intl.formatMessage({ id: 'noticeboard.form.attachments.existing' })}
                      </Typography>
                      {visibleExisting.map((att) => (
                        <Box
                          key={att.attachmentid}
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            mb: 1, px: 1.5, py: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: '10px',
                          }}
                        >
                          <Typography variant='body2'>
                            {att.description || att.attachmentid} ({(att.size / 1024 / 1024).toFixed(2)} MB)
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={() => this.removeExistingAttachment(att.attachmentid)}
                          >
                            <CloseIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      ))}
                    </Grid>
                  )}

                  {newFiles.length > 0 && (
                    <Grid item xs={12} sm={12}>
                      <Typography variant='subtitle2'>
                        {intl.formatMessage({ id: 'noticeboard.form.attachments.new' })}
                      </Typography>
                      {newFiles.map((file, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            mb: 1, px: 1.5, py: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: '10px',
                          }}
                        >
                          <Typography variant='body2'>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={() => this.removeNewFile(idx)}
                          >
                            <CloseIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      ))}
                    </Grid>
                  )}

                  <Grid item xs={12} sm={12} md={12} lg={12} style={{ marginTop: 15, display: 'flex', justifyContent: 'flex-end' }}>
                    <Grid container spacing={2} justifyContent='flex-end' style={{ justifyContent: 'flex-end' }}>
                      <Grid item xs={12} sm={4} md={3}>
                        <Button
                          fullWidth
                          variant='outlined'
                          className={classes.submit}
                          sx={{ textTransform: 'none', borderRadius: '10px' }}
                          onClick={() => this.backToList()}
                          disabled={saving}
                        >
                          {intl.formatMessage({ id: 'noticeboard.form.cancel' })}
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={4} md={3}>
                        <Button
                          fullWidth
                          variant='contained'
                          color='primary'
                          className={classes.submit}
                          sx={{ textTransform: 'none', borderRadius: '10px' }}
                          onClick={this.handleSave}
                          disabled={saving}
                        >
                          {intl.formatMessage({ id: 'noticeboard.form.save' })}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                </Box>
              )}

              {saving && (
                <Overlay>
                  <CircularProgress color='secondary' />
                </Overlay>
              )}

              <CustomizedSnackbars
                variant={snackVariant}
                message={snackMessage}
                isOpen={snackOpen}
                toClose={this.closeSnackbar}
              />

              <Dialog open={previewOpen} onClose={this.closePreview} maxWidth="md" fullWidth>
                <DialogTitle>{intl.formatMessage({ id: 'noticeboard.email.preview' })}</DialogTitle>
                <DialogContent>
                  <EmailDesignerPreview
                    preview={previewData}
                    loading={previewLoading}
                    error={null}
                    onRefresh={this.openPreview}
                    message={(id) => intl.formatMessage({ id })}
                  />
                </DialogContent>
              </Dialog>

              <Dialog open={restoreConfirmOpen} onClose={this.closeRestoreConfirm} maxWidth="xs" fullWidth>
                <DialogTitle>{intl.formatMessage({ id: 'noticeboard.email.template.restore' })}</DialogTitle>
                <DialogContent>
                  <Typography sx={{ fontSize: 14, color: '#555' }}>
                    {intl.formatMessage({ id: 'noticeboard.email.template.restoreConfirm' })}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={this.closeRestoreConfirm} sx={{ textTransform: 'none' }}>
                    {intl.formatMessage({ id: 'noticeboard.form.cancel' })}
                  </Button>
                  <Button variant="contained" color="warning" onClick={this.confirmRestoreFromTemplate} sx={{ textTransform: 'none' }}>
                    {intl.formatMessage({ id: 'noticeboard.email.template.restore' })}
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

AnnouncementEdit.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
});

export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(AnnouncementEdit)));
